'use client'

import { useRouter } from 'next/router'
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  MutableRefObject,
} from 'react'
import {
  navigationDirectionTracker,
  NavDirection,
} from '../../utils/navigationDirection'
import { popStateHeld } from '../../utils/popStateGuard'

interface NavigationContextValue {
  direction: NavDirection
  // For consumers that read direction inside a listener/effect they don't
  // want to tear down and re-register on every direction change (e.g. a
  // routeChangeStart handler registered once). Kept in sync at the exact
  // same points as `direction` itself, not via a separate effect watching
  // `direction` — that would land one render behind, reopening the same
  // timing gap `isVisible` (see Maavaron.tsx) exists to close.
  directionRef: MutableRefObject<NavDirection>
}

const defaultDirectionRef: MutableRefObject<NavDirection> = {
  current: 'forward',
}

const NavigationContext = createContext<NavigationContextValue>({
  direction: 'forward',
  directionRef: defaultDirectionRef,
})

interface NavigationProviderProps {
  children: ReactNode
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  const router = useRouter()
  const [direction, setDirection] = useState<NavDirection>('forward')
  const directionRef = useRef<NavDirection>('forward')

  useEffect(() => {
    // `beforePopState` is Next's own hook, called by its internal popstate
    // handler as the very first step — strictly before it touches `asPath`.
    // A `window.addEventListener('popstate', ...)` listener registered from
    // a React effect runs too late: Next's own listener is registered at
    // client bootstrap, long before any component mounts, so it always
    // fires first and can synchronously update `asPath` before ours gets a
    // turn. Resolving direction here closes that race instead of hoping to
    // win it.
    router.beforePopState(state => {
      // Next's own type for this callback omits `key`, but the runtime
      // object always carries it (see HistoryState in next/router).
      const key = (state as unknown as { key?: string }).key
      // a layer that closes on Back owns the step back to this page's own
      // entry: the page did not change, so Next stays out of it
      if (popStateHeld(key)) {
        return false
      }
      const next = navigationDirectionTracker.handlePopState(key)
      directionRef.current = next
      setDirection(next)
      return true
    })

    const onRouteChangeComplete = () => {
      const next = navigationDirectionTracker.handleRouteChangeComplete()
      directionRef.current = next
      setDirection(next)
    }
    router.events.on('routeChangeComplete', onRouteChangeComplete)

    return () => {
      router.beforePopState(() => true)
      router.events.off('routeChangeComplete', onRouteChangeComplete)
    }
    // `router` itself is a brand-new object on every navigation (see
    // makePublicRouterInstance in next/client/router.js) — only
    // `router.events` (a static property pointing at a shared EventEmitter)
    // is actually stable. Depending on `router` here would tear this effect
    // down and rebuild it on every route change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events])

  return (
    <NavigationContext.Provider value={{ direction, directionRef }}>
      {children}
    </NavigationContext.Provider>
  )
}

export function useNavigation() {
  const context = useContext(NavigationContext)
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return context
}
